
const css = `/* video-gallery.css */

/* Custom elements default to display:inline, which reports 0 height. */
video-gallery {
  display: block;
}

/* ── Loading skeleton (reserves gallery space to prevent CLS) ──
   The shimmer lives on a child span, not the tile itself: themes hide empty
   divs in this context, and .video-item's background shorthand would
   otherwise override the gradient. */
.video-skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  background: linear-gradient(90deg, #eceff1 25%, #f6f8f9 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: vg-skeleton-shimmer 1.4s ease infinite;
}

@keyframes vg-skeleton-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .video-skeleton-shimmer {
    animation: none;
  }
}

/* Skeleton tiles are plain shimmer — no title gradient or hover zoom.
   Doubled class keeps these ahead of the later .video-item rules. */
.video-item.video-skeleton-item::after {
  content: none;
}

.video-item.video-skeleton-item:hover {
  transform: none;
}

/* Single-row skeleton matching the slider layout: N columns with the same
   1rem gap/side padding as the Splide options, collapsing to 2 columns under
   768px and 1 column under 480px like the slider breakpoints. */
.video-skeleton-row {
  --vg-skeleton-cols: var(--vg-cols, 3);
  display: flex;
  gap: 1rem;
  padding: 0 1rem;
  overflow: hidden;
}

.video-skeleton-row .video-skeleton-slide {
  flex: 0 0 calc((100% - (var(--vg-skeleton-cols) - 1) * 1rem) / var(--vg-skeleton-cols));
}

@media (max-width: 768px) {
  .video-skeleton-row {
    --vg-skeleton-cols: min(var(--vg-cols, 3), 2);
    gap: 0.5rem;
    padding: 0 0.5rem;
  }

  .video-skeleton-row .video-skeleton-slide {
    flex-basis: calc((100% - (var(--vg-skeleton-cols) - 1) * 0.5rem) / var(--vg-skeleton-cols));
  }
}

@media (max-width: 480px) {
  .video-skeleton-row {
    --vg-skeleton-cols: 1;
  }

  /* Matches the slider's two-per-view phone breakpoint for opted-in shorts */
  .video-gallery.all-shorts.mobile-two-per-row .video-skeleton-row {
    --vg-skeleton-cols: min(var(--vg-cols, 3), 2);
  }
}

/* Container for the entire gallery */
.video-gallery {
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 0;
  padding-right: 0;
}

/* Grid layout for video thumbnails */
.video-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

/* Wrapper for individual video items */
.video-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Video item with consistent 16:9 aspect ratio.
   isolation contains the overlay/play-button z-indexes inside the tile so
   they never stack above theme elements like sticky headers. */
.video-item {
  position: relative;
  isolation: isolate;
  width: 100%;
  aspect-ratio: 16 / 9; /* Use aspect-ratio for consistent sizing */
  background: #f4f6f8;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.slider-video-item {
  isolation: isolate;
}

/* When every video in the gallery is a short, keep the tall 9:16 tiles.
   Mixed galleries fall through to the default 16:9 to avoid row-height blowout. */
.video-gallery.all-shorts .video-item,
.video-gallery.all-shorts .slider-video-item {
  aspect-ratio: 9 / 16;
}

.video-item:hover {
  transform: scale(1.02);
}

/* Full-width galleries: an edge-to-edge tile scaling up just clips at the
   viewport, so the tile stays put and the play button zooms instead. */
video-gallery.videoreel-full-width .video-item:hover,
video-gallery.videoreel-full-width .slider-video-item:hover {
  transform: none;
}

video-gallery.videoreel-full-width .video-item:hover .video-play-button,
video-gallery.videoreel-full-width .slider-video-item:hover .video-play-button {
  transform: translate(-50%, -50%) scale(1.18);
}

/* Viewport breakout. The liquid block renders the same rules inline when it
   knows the gallery is full width (block setting or dims metafield); these
   cover galleries where the class is added by the component at runtime, so
   the breakout works even when the metafield hasn't been mirrored yet.
   --videoreel-scrollbar is set by this script to the real vertical-scrollbar
   width; 100vw alone overhangs by that width on non-overlay scrollbars. */
video-gallery.videoreel-full-width {
  display: block;
  width: calc(100vw - var(--videoreel-scrollbar, 0px));
  max-width: none;
  margin-left: calc(50% - 50vw + var(--videoreel-scrollbar, 0px) / 2);
  margin-right: calc(50% - 50vw + var(--videoreel-scrollbar, 0px) / 2);
}

html:has(video-gallery.videoreel-full-width) {
  overflow-x: clip;
}

/* Contained sliders hang the arrows outside the track (-1.5rem), but on a
   full-width slider that lands outside the viewport where the root clip
   makes them unreachable — pull them inside the edges instead. */
video-gallery.videoreel-full-width .splide__arrow--prev {
  left: 1.2rem;
}

video-gallery.videoreel-full-width .splide__arrow--next {
  right: 1.2rem;
}

/* Edge-to-edge galleries read better with square corners. */
video-gallery.videoreel-full-width .video-item,
video-gallery.videoreel-full-width .slider-video-item,
video-gallery.videoreel-full-width .vg-ssr-tile,
video-gallery.videoreel-full-width .video-skeleton-item {
  border-radius: 0;
}

/* One item per view on a full-width gallery: drop all side padding so the
   video truly fills the viewport. The component toggles this class when
   videosPerRow is 1 (the liquid block renders the same rules from the dims
   metafield for the pre-JS placeholder). Splide sets its track padding as
   inline styles, hence the !important. */
video-gallery.videoreel-full-bleed .splide {
  padding-left: 0;
  padding-right: 0;
}

video-gallery.videoreel-full-bleed .splide__track {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

video-gallery.videoreel-full-bleed .vg-ssr-placeholder.vg-ssr-slider,
video-gallery.videoreel-full-bleed .video-skeleton-row {
  padding-left: 0;
  padding-right: 0;
}

/* Thumbnail image — !important guards against merchant theme/app rules
   like \`img { height: auto }\` from overriding the absolute fill. */
.video-thumbnail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  object-fit: cover;
  aspect-ratio: auto !important;
}

/* ── Autoplay preview (muted looping player over the thumbnail) ──
   Sits between the thumbnail and the gradient/title overlays. pointer-events
   none on both layers so every click reaches the tile and opens the modal. */
.video-autoplay-preview {
  /* !important: some themes hide empty divs, and this holder is empty until
     the IntersectionObserver mounts the iframe — display:none would keep it
     from ever intersecting. */
  display: block !important;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.video-autoplay-iframe {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Oversized and centered: the extra height pushes YouTube's title bar and
     watermark outside the clipped tile; width follows from the aspect ratio
     so the video still covers the full tile. */
  height: calc(100% + 120px);
  aspect-ratio: 16 / 9;
  border: 0;
  pointer-events: none;
  /* Invisible until the player reports PLAYING — the thumbnail underneath
     covers YouTube's black startup frames and loop restarts. */
  opacity: 0;
  transition: opacity 0.35s ease;
}

.video-gallery.all-shorts .video-autoplay-iframe {
  aspect-ratio: 9 / 16;
}

.video-autoplay-iframe.is-playing {
  opacity: 1;
}

/* Pause/resume toggle for the autoplay preview. The holder swallows pointer
   events, so the button re-enables its own. */
.video-autoplay-toggle {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: background 0.2s ease;
}

.video-autoplay-toggle:hover {
  background: rgba(0, 0, 0, 0.8);
}

.video-autoplay-toggle svg {
  width: 16px;
  height: 16px;
  display: block;
}

/* Overlay gradient for title */
.video-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0) 50%);
  pointer-events: none;
}

/* Bottom overlay container for title + product */
.video-bottom-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  z-index: 3;
}

/* Title of the video */
.video-item-title {
  /* Spacing is margin, not padding: overflow clips at the padding edge, so
     vertical padding would let the start of a third line show below the
     two-line clamp. */
  padding: 0;
  margin: 12px 16px;
  font-size: 1.4rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}

/* Merchant caption below the tile (outside the thumbnail). Inherits the
   theme's text color and font so it reads like the surrounding page.
   The wrapper is a size container so the caption scales with the tile's
   width: small tiles (many per row) get smaller text, a one-per-row tile
   gets larger. The plain font-size is the fallback for browsers without
   container query units. */
.video-item-wrapper {
  container-type: inline-size;
}

.video-item-description {
  margin: 0;
  padding: 0 2px;
  font-size: 0.95rem;
  font-size: clamp(0.8rem, 3.6cqi, 1.25rem);
  line-height: 1.5;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

/* Play button overlay */
.video-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex !important;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background 0.3s ease, transform 0.3s ease, opacity 0.3s ease;
}

/* While a preview is actually running on the tile (play-on-hover), the play
   button gets out of the way; it fades back when the preview pauses. */
.vg-preview-playing .video-play-button {
  opacity: 0;
}

/* Native preview for merchant-uploaded videos: plain cover fill — no
   oversizing needed since there's no player chrome to crop. */
.video-autoplay-native {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.35s ease;
  pointer-events: none;
}

.video-autoplay-native.is-playing {
  opacity: 1;
}

/* Modal player for uploaded videos reuses the iframe sizing box. */
video.video-iframe {
  object-fit: contain;
  background: #000;
}

/* Modal overlay */
.video-modal-global {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease;
}

/* Modal content container */
.video-modal-content-global {
  position: relative;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: visible;
  aspect-ratio: 16 / 9;
  min-height: 80vh;
}

.video-modal-content-global.short {
  aspect-ratio: auto;
  min-height: auto;
  max-height: 90vh;
}

.video-modal-content-global.has-products {
  max-height: 80vh;
  min-height: 80vh;
}

.video-modal-content-global.has-products.short {
  min-height: auto;
  max-height: 90vh;
}

/* Outer wrapper for video + product bar in modal */
.video-and-products {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 1280px;
}

.video-and-products.short {
  max-width: calc(80vh * 9 / 16);
}

/* Wrapper for the iframe with aspect ratio */
.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 1280px; /* Max width for normal videos */
  aspect-ratio: 16 / 9; /* 16:9 Aspect Ratio for normal videos */
  display: flex;
  justify-content: center;
  align-items: center;
}

.video-wrapper.short {
  max-width: calc(80vh * 9 / 16);
  height: 80vh;
  width: auto;
  aspect-ratio: 9 / 16; /* 9:16 Aspect Ratio for shorts */
}

/* Iframe styling */
.video-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

/* Close button for the modal */
.video-modal-close-global {
  position: fixed;
  top: 20px;
  right: 20px;
  color: white;
  font-size: 32px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  z-index: 10000;
  transition: background 0.3s ease;
}

.video-modal-close-global:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* Fade-in animation for the modal */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive Design */
@media (max-width: 768px) {
  .video-modal-content-global {
    padding: 0;
    min-height: auto;
    aspect-ratio: auto;
    max-height: none;
    width: 100%;
  }

  .video-modal-content-global.has-products {
    min-height: auto;
    max-height: none;
  }

  .video-modal-close-global {
    top: 10px;
    right: 10px;
  }

  .video-wrapper {
    max-width: 100%;
    aspect-ratio: 16 / 9;
  }

  .video-wrapper.short {
    height: 75vh;
    max-width: calc(75vh * 9 / 16);
    width: auto;
    aspect-ratio: 9 / 16;
  }

  .video-and-products.short {
    max-width: calc(75vh * 9 / 16);
  }

  .video-modal-content-global.short {
    max-height: 85vh;
  }

  /* 16:9 tiles with a product card: the title + card overlay covers the lower
     half, so center the play button in the free area above it instead of the
     tile's true center. */
  .video-item:not(.is-short):has(.video-thumbnail-product) .video-play-button,
  .slider-video-item:not(.is-short):has(.video-thumbnail-product) .video-play-button {
    top: 25%;
  }
}

/* Additional Styles for Video Items */
.video-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.video-title-global {
  display: none;
}

/* Responsive grid adjustments */
@media (max-width: 768px) {
  .video-container {
    grid-template-columns: 1fr !important; /* Force single column on mobile */
  }

  /* Opt-in for all-shorts galleries: two tall tiles side by side on phones */
  .video-gallery.all-shorts.mobile-two-per-row .video-container {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}
/* ── Shared glass product card styles ── */
.video-thumbnail-product-glass {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: rgba(53, 53, 52, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
}

.video-thumbnail-product-row {
  display: flex;
  align-items: stretch;
}

.video-thumbnail-product-image {
  width: 64px;
  align-self: stretch;
  min-height: 64px;
  height: auto;
  border-radius: 0;
  border: none;
  object-fit: cover;
  flex-shrink: 0;
  background: #ffffff;
}

.video-thumbnail-product-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  flex: 1;
  padding: 8px 14px;
}

.video-thumbnail-product-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
}

.video-thumbnail-product-price {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
}

/* ── Thumbnail product overlay ── */
.video-thumbnail-product {
  padding: 0 16px 16px;
  box-sizing: border-box;
  text-decoration: none;
  display: block;
}

/* ── Pagination dots ── */
.video-product-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 8px 0 0;
}

.video-product-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.35);
  padding: 0;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.video-product-dot.active {
  background: rgba(255, 255, 255, 0.9);
  transform: scale(1.2);
}

/* ── Modal product bar ── */
.video-products-bar {
  margin-top: 12px;
  width: 100%;
  box-sizing: border-box;
}

/* ── Modal product sidebar (desktop) ──
   Doran-style split layout: video on the left, a white panel with a product
   grid on the right. Only used when the viewport is wide enough; below the
   breakpoint the compact bottom bar takes over. */
.video-products-sidebar {
  display: none;
}

@media (min-width: 1024px) {
  .video-and-products.with-sidebar {
    flex-direction: row;
    align-items: stretch;
    width: auto;
    max-width: calc(100vw - 80px);
    border-radius: 12px;
    overflow: hidden;
    background: #000;
  }

  .video-and-products.with-sidebar.short {
    max-width: calc(100vw - 80px);
  }

  .video-modal-content-global.has-products {
    aspect-ratio: auto;
    min-height: auto;
    max-height: none;
  }

  .video-and-products.with-sidebar .video-wrapper {
    width: min(calc(80vh * 16 / 9), calc(100vw - 460px));
    max-width: none;
    aspect-ratio: 16 / 9;
    background: #000;
  }

  .video-and-products.with-sidebar .video-wrapper.short {
    width: calc(80vh * 9 / 16);
    height: 80vh;
    aspect-ratio: 9 / 16;
  }

  .video-and-products.with-sidebar .video-iframe {
    border-radius: 0;
  }

  .video-and-products.with-sidebar .video-products-bar {
    display: none;
  }

  .video-and-products.with-sidebar .video-products-sidebar {
    display: flex;
    flex-direction: column;
    width: 360px;
    flex-shrink: 0;
    background: #ffffff;
    max-height: 80vh;
  }
}

.video-products-sidebar-header {
  padding: 16px 20px;
  font-size: 15px;
  font-weight: 600;
  color: #111111;
  border-bottom: 1px solid #ececec;
  flex-shrink: 0;
}

.video-products-sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.video-products-sidebar-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.video-sidebar-product-card {
  display: block;
  text-decoration: none;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.video-sidebar-product-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.video-sidebar-product-image {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
  background: #f6f6f4;
}

.video-sidebar-product-info {
  padding: 10px 12px;
}

.video-sidebar-product-title {
  font-size: 13px;
  line-height: 1.35;
  color: #111111;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-sidebar-product-price {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #111111;
}

.video-sidebar-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.video-products-sidebar-grid + .video-sidebar-links {
  border-top: 1px solid #ececec;
  padding-top: 16px;
}

.video-sidebar-link-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 13px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  background: #f5f5f3;
  color: #111111;
  border: 1px solid #e8e8e8;
  transition: background 0.15s ease;
}

.video-sidebar-link-cta:hover {
  background: #ececea;
}

/* ── Link CTAs ── */
.video-thumbnail-links {
  padding: 0 16px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.video-thumbnail-link-cta {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 14px 22px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  background: rgba(53, 53, 52, 0.6);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform 0.15s ease, filter 0.2s ease;
  max-width: 100%;
}

.video-thumbnail-link-cta:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.video-thumbnail-link-cta-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.video-thumbnail-link-cta-arrow {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .video-thumbnail-links {
    padding: 0 10px 10px;
    gap: 6px;
  }

  .video-thumbnail-link-cta {
    padding: 12px 18px;
    font-size: 13px;
    gap: 20px;
    border-radius: 12px;
  }
}


@media (max-width: 768px) {
  .video-thumbnail-product {
    padding: 0 10px 10px;
  }

  .video-thumbnail-product-glass {
    border-radius: 10px;
  }

  .video-thumbnail-product-image {
    width: 52px;
    min-height: 52px;
  }

  .video-thumbnail-product-info {
    padding: 6px 10px;
  }

  .video-thumbnail-product-title {
    font-size: 14px;
  }

  .video-thumbnail-product-price {
    font-size: 14px;
  }

}

div.shopify-app-block[data-shopify-editor-block*="videoreel_gallery"], div[id*="__videoreel_gallery_"] {
  width: 100% !important;
}`;
export default css;
